/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAddressAddItem, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address';
import { IAddressShowItem } from '@msdyn365-commerce-modules/address';
import { ICheckoutBillingAddressViewProps } from './dobbies-checkout-new-billing-address';
import { getCartState, getCheckoutState, ICartState, ICheckoutState } from '@msdyn365-commerce/global-state';
import PromoCodeComponent from '../../themes/dobbies/views/components/promocode.component';
import { IAddressShowProps } from './components/address-show';
import { data } from 'azure-maps-control';
import { getRawCartSubtotalAmount } from '../../themes/dobbies/utilities/order-summary-utils';

/**
 * Address show component.
 * @param param0 - Root param.
 * @param param0.AddressDetail - Address detail.
 * @param param0.items - IAddressShowItem[].
 * @returns - Address Node.
 */
const AddressShow: React.FC<IAddressShowProps> = ({ AddressDetail, items, data }) => {
    const email = data?.checkout?.result?.shippingAddress?.Email;
    return (
        <Node {...AddressDetail}>
            {items.map((item: IAddressShowItem, index) => {
                const { key, description } = item;
                const isNotCountry = key.toLowerCase() !== 'threeletterisoregionname';
                const isSecondLast = index === items.length - 2;
                return (
                    <React.Fragment key={key}>
                        {isNotCountry ? item.description : <></>}
                        {isSecondLast && email ? (
                            <p className='msc-address-detail__item msc-address-detail__item-email msc-address-detail__item-newline'>
                                {email}
                            </p>
                        ) : (
                            <></>
                        )}
                    </React.Fragment>
                );
            })}
        </Node>
    );
};

/**
 * Address Add Update Component.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address Heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - IAddressError.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns - AddressForm Node.
 */
const AddressAddUpdate: React.FC<IAddressAddUpdateProps> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton
}) => {
    return (
        <Node {...AddressForm}>
            {heading}
            {items.map((item: IAddressAddItem) => {
                const { AddressItem, key, label, alert, input } = item;
                return (
                    <Node {...AddressItem} key={key}>
                        {label}
                        {alert}
                        {input}
                    </Node>
                );
            })}
            {hasError && (
                <Node {...error.AddressError}>
                    {error.title}
                    {error.message}
                </Node>
            )}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

/**
 * Checkout Billing Address View Component.
 * @param props - Props.
 * @returns - CheckoutBillingAddress Module.
 */
const CheckoutBillingAddressView: React.FC<ICheckoutBillingAddressViewProps> = props => {
    const {
        CheckoutBillingAddress,
        checkoutErrorRef,
        alert,
        viewState,
        heading,
        sameAsShippingCheckbox,
        showAddress,
        showAddOrUpdateAddress
    } = props;

    const [checkoutCartState, setCheckoutCartState] = React.useState<ICheckoutState | undefined>(undefined);
    const [cartState, setCartState] = React.useState<ICartState | undefined>();
    const [delCharges, setDelCharges] = React.useState<number>(0);

    React.useEffect(() => {
        getCheckoutState(props.context.actionContext)
            .then(res => {
                setCheckoutCartState(res);
            })
            .catch(e => {
                console.log('Error', e);
            });
        getCartState(props.context.actionContext).then(cartState => {
            setCartState(cartState);
            let deliveryCharges = 0;
            cartState.cart.ChargeLines?.map(chargeLine => {
                deliveryCharges += chargeLine.CalculatedAmount || 0;
            });
            setDelCharges(deliveryCharges);
        });
    }, []);

    // Once charges have been updated, calculate total delivery charges
    React.useEffect(() => {
        if (props.data.checkoutModulesSharedState.result?.isChargesUpdated) {
            getCheckoutState(props.context.actionContext).then(checkoutState => {
                let charges = 0;
                checkoutState.checkoutCart?.cart.ChargeLines?.map(chargeLine => {
                    charges += chargeLine.CalculatedAmount || 0;
                });
                setDelCharges(charges);
            });
        }
    }, [props.data.checkoutModulesSharedState.result]);

    const freeDelivery: number = props.config.freeDeliveryMilestone!;
    let spendX: number | Boolean = freeDelivery - checkoutCartState?.checkoutCart.cart.SubtotalAmount!;
    let numWithZeroes;
    if (spendX < 0 || delCharges === props.context.request.app.config.chargesForHDItems) {
        spendX = false;
    } else {
        numWithZeroes = parseFloat(spendX.toString()).toFixed(2);
    }

    let hasCnc = false;
    checkoutCartState?.checkoutCart.cart.CartLines?.map(cl => {
        if (cl.FulfillmentStoreId) {
            hasCnc = true;
        }
    });

    const cncItems = checkoutCartState?.checkoutCart?.cart.CartLines?.filter(
        line => line.DeliveryMode === props.context.request.channel?.PickupDeliveryModeCode
    );
    const isCNCOnlyCart = cncItems && cncItems?.length === checkoutCartState?.checkoutCart.cart.CartLines?.length;

    const hasHDItem = props.data.deliveryOptions.result?.find(option =>
        option.DeliveryOptions?.find(del => del.Code === 'HD' || del.Code === 'HD Presale')
    );

    const cartDeliveryModes = cartState?.cart.CartLines?.map(line => line.DeliveryMode).filter(
        mode => mode !== 'Common MOD' && mode !== 'ClickCol'
    );

    const isOnlyHDCart = cartDeliveryModes?.every(mode => mode === 'HD' || mode === 'HD Presale');

    const isMixedCart =
        cartDeliveryModes?.some(mode => mode === 'HD' || mode === 'HD Presale') &&
        cartDeliveryModes?.some(mode => mode !== 'HD' && mode !== 'HD Presale');

    const rawSubtotal = getRawCartSubtotalAmount(checkoutCartState?.checkoutCart.cart);
    const total = checkoutCartState?.checkoutCart.cart.TotalAmount;

    const HDDeliveryCharges =
        delCharges !== undefined ? (delCharges === 0 ? 'Free' : props.context.cultureFormatter.formatCurrency(delCharges)) : undefined;

    return (
        <>
            <Module {...CheckoutBillingAddress} ref={checkoutErrorRef}>
                {viewState.isShowSameAsShippingCheckbox && sameAsShippingCheckbox}
                {viewState.isShowAddOrUpdateAddress ? <p>Please confirm your billing address.</p> : <></>}
                {viewState.isShowAddress && <AddressShow {...showAddress} data={props.data} />}
                {viewState.isShowAddOrUpdateAddress && <AddressAddUpdate {...showAddOrUpdateAddress} />}
                {viewState.isShowAddOrUpdateAddress && props.renderAddressSuggestions}
            </Module>
            <div className='msc-order-summary-wrapper'>
                <div className='msc-order-summary__content'>
                    <h2 className='msc-order-summary__heading'>
                        Order summary (
                        <span className='msc-order-summary__heading-items-count'>
                            {checkoutCartState?.checkoutCart.cart.TotalItems}{' '}
                            {checkoutCartState?.checkoutCart.cart.TotalItems && checkoutCartState?.checkoutCart.cart.TotalItems !== 1
                                ? 'items'
                                : 'item'}
                            )
                        </span>
                    </h2>
                    <div className='msc-order-summary__items'>
                        <p className='msc-order-summary__line-sub-total'>
                            <span className='msc-order-summary__label'>Subtotal:</span>
                            <span className='msc-order-summary__value'>
                                {rawSubtotal !== undefined && props?.context?.cultureFormatter.formatCurrency(rawSubtotal)}
                            </span>
                        </p>

                        <div className='msc-order-summary__line-delivery-cost'>
                            {!isCNCOnlyCart && (
                                <p>
                                    <span className='msc-order-summary__label'>Delivery cost:</span>
                                    <span className='msc-order-summary__value'>
                                        {hasHDItem
                                            ? `${HDDeliveryCharges}`
                                            : (checkoutCartState?.checkoutCart?.cart?.SubtotalAmount &&
                                                  checkoutCartState?.checkoutCart?.cart?.SubtotalAmount > freeDelivery) ||
                                              delCharges === 0
                                            ? 'Free'
                                            : `£${delCharges}`}
                                    </span>
                                </p>
                            )}
                            {hasHDItem && isOnlyHDCart ? <p className='large-item-label'>Large item delivery.</p> : <></>}
                            {hasHDItem && isMixedCart ? <p className='large-item-label'>Mixed delivery methods.</p> : <></>}

                            {spendX && !isCNCOnlyCart ? (
                                <div className='promptSpend'>Spend £{numWithZeroes} more for free delivery. Eligible items only.</div>
                            ) : (
                                <></>
                            )}
                            {!spendX && !hasHDItem && !isCNCOnlyCart ? (
                                <p className='promptSpend'>You have qualified for free delivery.</p>
                            ) : (
                                <></>
                            )}
                            {hasCnc ? (
                                <p>
                                    <span className='msc-order-summary__label'>Click & collect:</span>
                                    <span className='msc-order-summary__value'>Free</span>
                                </p>
                            ) : (
                                <></>
                            )}
                        </div>
                        <PromoCodeComponent
                            cart={checkoutCartState?.checkoutCart}
                            promoCodeHeadingText={'Promo code'}
                            promoPlaceholderText={'Enter promo code'}
                            promoCodeApplyButtonText={'Apply'}
                            appliedPromoCodeHeadingText={'Promo code applied:'}
                            collapseTimeOut={350}
                            removePromoAriaLabelFormat={'{0} {1}'}
                            removePromoText={'Remove'}
                            invalidPromoCodeErrorText={'Invalid promo code.'}
                            failedToAddPromoCodeErrorText={'Failed to add promo code'}
                            failedToRemovePromoCodeErrorText={'Failed to remove promo code'}
                            duplicatePromoCodeErrorText={'Promo code already applied'}
                            context={props.context}
                            id={props.id}
                            typeName={props.typeName}
                            data={{}}
                        />
                        <p className='msc-order-summary__line-total'>
                            <span className='msc-order-summary__label'>Total </span>
                            <span className='msc-price msc-order-summary__value'>
                                <span className='msc-price__actual ' itemProp='price'>
                                    {total !== undefined && props?.context?.cultureFormatter.formatCurrency(total)}
                                </span>
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CheckoutBillingAddressView;
